import buffer from '@turf/buffer'
import unkinkPolygon from '@turf/unkink-polygon'
import { polygon as turfPolygon } from '@turf/helpers'

// Byte / vertex budget for the serialised expanded ring. A dense county outline
// (Kent, Cornwall ~1,300+ vertices) buffered and serialised verbatim produces a
// 30+ KB filter[expanded_polygon], blowing the ~8 KB server URL limit
// (NGSSPT-189 D1/D4). We Douglas-Peucker the buffered ring down until the
// serialised ring fits under budget. The BYTE budget is the binding, ultimate
// constraint (the polygon param must stay <= 4000 bytes so the total request URL
// clears the ~8 KB limit with headroom); the vertex cap is a secondary guard.
// The serialised ring is pure ASCII (digits, brackets, commas, minus, dot) so
// its JSON string length equals its UTF-8 byte length.
const MAX_RING_VERTICES = 250
const MAX_SERIALISED_BYTES = 4000

// Adaptive-epsilon search, in degrees. Start fine (~50 m) so low-vertex rings
// are left essentially untouched, then coarsen by SIMPLIFY_EPSILON_GROWTH each
// step until the serialised ring fits. By ~step 5 the tolerance is ~0.005 deg
// (~500 m), which clears the budget on the worst cached county once buffered, so
// the ladder normally terminates well before then. The byte budget is the
// ULTIMATE gate, though: a pathological ring still over budget keeps escalating
// past that point rather than shipping an over-budget URL. SIMPLIFY_MAX_STEPS
// caps the iterations so the loop always terminates.
const SIMPLIFY_EPSILON_START = 0.0005
const SIMPLIFY_EPSILON_GROWTH = 1.6
const SIMPLIFY_MAX_STEPS = 12

// Decimal places used when measuring the serialised ring. Mirrors the default in
// pathsToLngLatRing so the budget check reflects what actually goes on the wire.
const SERIALISE_PRECISION = 5

/**
 * Perpendicular distance from point p to the line through a and b, in the same
 * (lng, lat) coordinate units. Used by the Douglas-Peucker recursion.
 */
function perpendicularDistance(p, a, b) {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  if (dx === 0 && dy === 0) {
    return Math.hypot(p[0] - a[0], p[1] - a[1])
  }
  const numerator = Math.abs(dy * p[0] - dx * p[1] + b[0] * a[1] - b[1] * a[0])
  return numerator / Math.hypot(dx, dy)
}

/**
 * Douglas-Peucker on an open polyline of [lng, lat] points. Returns the
 * simplified polyline (endpoints always preserved).
 */
function douglasPeucker(points, epsilon) {
  if (points.length < 3) return points.slice()

  let maxDistance = 0
  let index = 0
  const end = points.length - 1
  for (let i = 1; i < end; i += 1) {
    const distance = perpendicularDistance(points[i], points[0], points[end])
    if (distance > maxDistance) {
      maxDistance = distance
      index = i
    }
  }

  if (maxDistance > epsilon) {
    const left = douglasPeucker(points.slice(0, index + 1), epsilon)
    const right = douglasPeucker(points.slice(index), epsilon)
    // Drop the duplicated join point (last of left === first of right).
    return left.slice(0, -1).concat(right)
  }
  return [points[0], points[end]]
}

/**
 * Simplify a closed [lng, lat] ring with Douglas-Peucker at the given epsilon,
 * keeping the ring closed (first === last). Operates on the open polyline
 * (ring without its duplicate closing point) and re-closes afterwards, so both
 * the first and last vertex are preserved and closure always holds.
 */
function simplifyClosedRing(ring, epsilon) {
  const open = ring.slice(0, ring.length - 1)
  const simplifiedOpen = douglasPeucker(open, epsilon)
  return simplifiedOpen.concat([simplifiedOpen[0].slice()])
}

/**
 * Measure the serialised byte size of a [lng, lat] ring the way it ships on the
 * wire: round to SERIALISE_PRECISION decimals, then JSON.stringify. The output
 * is pure ASCII so string length equals UTF-8 byte length.
 */
function serialisedByteLength(ring) {
  const factor = 10 ** SERIALISE_PRECISION
  const round = (n) => Math.round(n * factor) / factor
  const rounded = ring.map(([lng, lat]) => [round(lng), round(lat)])
  return JSON.stringify(rounded).length
}

/**
 * Reduce a closed [lng, lat] ring until its serialised form fits the byte budget
 * (the BINDING, ultimate constraint) and the vertex cap.
 *
 * Purely byte-driven: escalate the Douglas-Peucker tolerance from a non-zero
 * floor (SIMPLIFY_EPSILON_START) by SIMPLIFY_EPSILON_GROWTH each step,
 * re-simplifying, because small intricate counties (Isle of Wight, City of
 * London at r=1) can sit near the budget edge near the floor tolerance. The
 * ~0.005 deg point (about step 5) is the expected ceiling, but once a
 * pathological ring is still over budget we keep escalating past it, because
 * shipping an over-budget URL is the failure we are preventing.
 * SIMPLIFY_MAX_STEPS caps the iterations so the loop always terminates.
 *
 * The closed-ring >= 4-point floor always holds: we stop at the last valid
 * closed ring (>= 4 points) and never emit a degenerate one. Returning a ring
 * still over budget only happens if even that last valid ring exceeds the budget
 * (a surfaced pathological failure), and only when it is still smaller than the
 * unsimplified input; we never silently ship the full ring when escalation could
 * have helped, and we never ship a degenerate ring.
 */
function fitRingToBudget(ring) {
  const withinBudget = (candidate) => candidate.length <= MAX_RING_VERTICES
    && serialisedByteLength(candidate) <= MAX_SERIALISED_BYTES

  if (withinBudget(ring)) return ring

  let best = null
  let epsilon = SIMPLIFY_EPSILON_START
  for (let step = 0; step < SIMPLIFY_MAX_STEPS; step += 1) {
    const candidate = simplifyClosedRing(ring, epsilon)
    // Stop before collapsing the shape: keep the last valid closed ring.
    if (candidate.length < 4) break
    best = candidate
    if (withinBudget(candidate)) return candidate
    epsilon *= SIMPLIFY_EPSILON_GROWTH
  }

  // Could not get under budget without collapsing the shape. Return the smallest
  // valid closed ring we found (a surfaced over-budget failure, never a
  // degenerate ring); if we never produced one, fall back to the unsimplified
  // input. Either way the >= 4-point closed-ring invariant holds.
  return best ?? ring
}

/**
 * Planar shoelace area of a closed [lng, lat] ring. Only used to RANK rings
 * of the same county, so the lat-distortion of treating degrees as planar
 * units is irrelevant.
 */
function ringArea(ring) {
  let area = 0
  for (let i = 0; i < ring.length - 1; i += 1) {
    area += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1]
  }
  return Math.abs(area / 2)
}

/**
 * Split a possibly self-intersecting ring into simple polygons and return
 * the largest by area as a turf Polygon feature (the county itself; bow-tie
 * slivers are tiny). Falls back to the unsplit polygon if unkink fails.
 */
function largestSimplePiece(ring) {
  const feature = turfPolygon([ring])
  let pieces
  try {
    pieces = unkinkPolygon(feature)?.features ?? []
  } catch {
    return feature
  }
  if (pieces.length <= 1) return feature

  return pieces.reduce((best, piece) => {
    const candidate = piece?.geometry?.coordinates?.[0]
    if (!Array.isArray(candidate)) return best
    const bestRing = best?.geometry?.coordinates?.[0]
    return ringArea(candidate) > (bestRing ? ringArea(bestRing) : 0) ? piece : best
  }, null) ?? feature
}

/**
 * Largest outer ring of a (Multi)Polygon by area. Vertex count is the wrong
 * rank: a dense coastal sliver can out-vertex the actual county blob.
 */
function largestOuterRingByArea(geometry) {
  if (geometry.type === 'Polygon') {
    return geometry.coordinates?.[0] ?? null
  }
  if (geometry.type !== 'MultiPolygon') return null

  return (geometry.coordinates ?? []).reduce((best, polygon) => {
    const candidate = polygon?.[0]
    if (!Array.isArray(candidate)) return best
    return ringArea(candidate) > (best ? ringArea(best) : 0) ? candidate : best
  }, null)
}

/**
 * Inflates a Google-Maps style polygon path by N miles using turf-buffer.
 *
 * Input is the same shape we store in Vuex: `[{lat, lng}, ...]`. We
 * convert to GeoJSON for turf, run the buffer, and convert the result
 * back to the Google-Maps shape.
 *
 * Returns `null` if the input is empty, the radius is non-positive,
 * or turf cannot produce a buffered ring (e.g. degenerate geometry).
 *
 * @param {Array<{lat: number, lng: number}>} paths Closed ring; first === last is fine but not required.
 * @param {number} miles Positive radius in statute miles.
 * @returns {Array<{lat: number, lng: number}>|null}
 */
export function bufferRing(paths, miles) {
  if (!Array.isArray(paths) || paths.length < 3) return null
  if (!Number.isFinite(miles) || miles <= 0) return null

  const ring = paths.map((p) => [p.lng, p.lat])
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (first[0] !== last[0] || first[1] !== last[1]) {
    ring.push([first[0], first[1]])
  }

  let buffered
  try {
    // Cached county outlines can be self-intersecting (the server-side
    // simplifier leaves bow-ties on jagged coastlines; Cornwall has 5).
    // Buffering an invalid polygon shatters it into ~100 coastal fragments,
    // so unkink first and buffer the largest simple piece; the bow-tie
    // slivers are sub-0.1% artefacts of the invalid geometry.
    buffered = buffer(largestSimplePiece(ring), miles, { units: 'miles' })
  } catch {
    return null
  }
  if (!buffered?.geometry) return null

  const outerRing = largestOuterRingByArea(buffered.geometry)
  if (!Array.isArray(outerRing) || outerRing.length < 4) return null

  // Decimate the buffered ring so its serialised form stays under the URL
  // budget. Done here, ONCE, as the last step, so every consumer (the visual
  // overlay in SearchAreaPolygon.vue, the searchAreaViewport bbox in
  // GoogleMapLoader, and the filter[expanded_polygon] ring sent to the API) gets
  // the same simplified ring. serialiseExpandedPolygon must NOT simplify again;
  // it only rounds this ring to 5dp (~1 m), so the API copy is this exact ring
  // at ~1 m precision, not a separately simplified one.
  const fitted = fitRingToBudget(outerRing)

  return fitted.map(([lng, lat]) => ({ lat, lng }))
}

/**
 * Convert a Google-Maps style ring into the [lng, lat] form the API
 * expects for `filter[expanded_polygon]`.
 *
 * Coordinates are rounded to a fixed precision before serialising. The
 * default of 5 decimal places gives ~1.1m of geographic precision at UK
 * latitudes: well below the polygon-vertex spacing of a county outline
 * and far more than enough for an Elasticsearch geo_polygon filter.
 * Rounding keeps the JSON payload (and the resulting URL) small enough
 * to clear nginx / Apache default URL-length limits (~8KB). Note that
 * vertex COUNT, not coordinate precision, dominates the payload size, so
 * the heavy lifting for the URL budget is the Douglas-Peucker decimation
 * done inside bufferRing; this rounding is a secondary trim.
 *
 * @param {Array<{lat: number, lng: number}>} paths
 * @param {number} [precision=5] Decimal places to keep on each coord.
 * @returns {Array<[number, number]>|null}
 */
export function pathsToLngLatRing(paths, precision = 5) {
  if (!Array.isArray(paths) || paths.length < 4) return null

  const factor = 10 ** precision
  const round = (n) => Math.round(n * factor) / factor

  const ring = paths.map((p) => [round(p.lng), round(p.lat)])
  const first = ring[0]
  const last = ring[ring.length - 1]
  if (first[0] !== last[0] || first[1] !== last[1]) {
    ring.push([first[0], first[1]])
  }
  return ring
}
